import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useProviderLoader } from '@/Providers'
import { ContainerGroupField } from '@/components/common'
import { Text } from '@/components/common/UI'
import { Icons } from '@/components/icons'
import { Button, Chip, Link } from '@/components/nextui'
import mockDetails from '@/data/forms/mockInterviewDetails.json'
import { useAuthHook } from '@/hooks'
import { FormsGroupsProps, InterviewDetails } from '@/types/forms'

export function InterviewDetailsView({
    onNext,
    onPrevious
}: FormsGroupsProps): JSX.Element {
    const { idAspirant } = useAuthHook()
    const { t } = useTranslation()
    const { showLoader, hideLoader } = useProviderLoader()
    const [details, setDetails] = useState<InterviewDetails>()
    const text = 'interview.viewDetails'

    useEffect(() => {
        const fetchInterviewData = async (): Promise<Element | void> => {
            if (!idAspirant) {
                return
            }

            showLoader()
            try {
                // const response = await aspirantsApi.getInterviewData(idAspirant)
                const response = mockDetails

                setDetails(response)
            } catch (error) {
                console.error('Error fetching interview data:', error)
            } finally {
                hideLoader()
            }
        }

        fetchInterviewData()
    }, [idAspirant])

    return (
        <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
        >
            {details && (
                <section>
                    <ContainerGroupField
                        tittleGroup={t('forms.groups.interview')}
                        onlyOneField
                    >
                        <div className="space-y-4">
                            <div className="flex items-center space-x-8">
                                <Text text={t(`${text}.state`)} />
                                <Chip
                                    color={`${
                                        details.interviewStatus === 'completed'
                                            ? 'primary'
                                            : 'warning'
                                    }`}
                                >
                                    {t(details.interviewStatus)}
                                </Chip>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-2">
                                <div className="space-y-2">
                                    <Text
                                        text={t(`${text}.date`)}
                                        className="text-default-500"
                                    />

                                    <Text text={details.interviewDate} />
                                </div>
                                <div className="space-y-2">
                                    <Text
                                        text={t(`${text}.time`)}
                                        className="text-default-500"
                                    />

                                    <Text text={details.interviewTime} />
                                </div>
                                <div className="space-y-2">
                                    <Text
                                        text={t(`${text}.interviewer`)}
                                        className="text-default-500"
                                    />

                                    <Text text={details.interviewerName} />
                                </div>

                                <div className="space-y-2">
                                    <Text
                                        text={t(`${text}.location`)}
                                        className="text-default-500"
                                    />

                                    <Text text={details.interviewPlace} />
                                </div>
                            </div>

                            {details.meetingLink && (
                                <div className="mt-4 p-4 bg-primary/10 rounded-lg">
                                    <Text
                                        text={t(`${text}.link`)}
                                        className="text-default-500"
                                    />
                                    <a
                                        href={details.meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline break-all"
                                    >
                                        <Text text={details.meetingLink} />
                                    </a>
                                </div>
                            )}

                            <div className="mt-4">
                                <Text
                                    text={t('support')}
                                    className="text-default-500"
                                />
                                <p className="flex items-center gap-2">
                                    <Icons.Support className="w-4 h-4" />
                                    <Link
                                        href={`mailto:${details.emailSupport}`}
                                    >
                                        <Text
                                            text={details.emailSupport}
                                            className="text-primary hover:underline"
                                        />
                                    </Link>
                                </p>
                            </div>

                            {details.interviewerNotes && (
                                <div className="mt-4 p-4 bg-default-100 rounded-lg">
                                    <Text
                                        text={t(`${text}.interviewNotes`)}
                                        className="text-default-500"
                                    />

                                    <Text
                                        text={details.interviewerNotes}
                                        className="italic"
                                    />
                                </div>
                            )}
                        </div>
                    </ContainerGroupField>
                    {details.interviewStatus !== 'completed' && (
                        <div className="flex justify-between pt-4">
                            <Button
                                color="secondary"
                                onPress={onPrevious}
                            >
                                {t('forms.ui.previous')}
                            </Button>

                            <Button
                                color="primary"
                                startContent={<Icons.BookIcon />}
                                className="hover:scale-105 transition-transform"
                                onPress={onNext}
                            >
                                {t(`${text}.reassignInterview`)}
                            </Button>
                        </div>
                    )}
                </section>
            )}
        </motion.div>
    )
}
